import { AfterViewInit, Component, NgZone, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AttendeesService } from '../../../_services/_auth/attendees.service';
import {
  ICustomApp,
  IEventGeneralSettings,
  ILogin,
  IRegistration,
  IRegistrationEventbrite,
  IRegistrationIFrame,
  IRegistrationStandard,
  REGISTER_FIELDS_TYPES,
  RegistrationType
} from '@sv/interfaces';
import { CustomAppService } from '../../../_services/_content/customApp.service';
import { TicketcodeAuthService } from '../../../_services/_auth/ticketcode-auth.service';
import { RegistrationService } from 'src/app/_services/_content/registration.service';
import { InitAppService } from 'src/app/_services/init-app.service';
import { MatDialog } from '@angular/material/dialog';
import { PasswordDialogComponent } from './password-dialog/password-dialog.component';
import { LoginService } from 'src/app/_services/_content/login.service';

declare function registerEvent(eventId, onOrderComplete): void;

@Component({
  selector: 'app-registration-form',
  templateUrl: './registration-form.component.html',
  styleUrls: ['./registration-form.component.scss']
})
export class RegistrationFormComponent implements OnInit, AfterViewInit {
  registration: IRegistration;
  settings;
  customApp: ICustomApp;
  registerForm: FormGroup;
  url = '';
  login: ILogin;
  toLogin = false;
  errorEmailExists = false;

  email: string;
  hidePw = true;

  clickedRegister = false;
  isLoading = true;

  REGISTER_FIELDS_TYPES = REGISTER_FIELDS_TYPES;

  constructor(
    public attendeeService: AttendeesService,
    private router: Router,
    private customAppService: CustomAppService,
    private ticketcodeAuthService: TicketcodeAuthService,
    private registrationService: RegistrationService,
    private loginService: LoginService,
    private initAppService: InitAppService,
    private dialog: MatDialog,
    private ngZone: NgZone,
    private route: ActivatedRoute
  ) {}
  ngAfterViewInit(): void {
    if (this.registration.type === RegistrationType.EVENTBRITE) {
      const that = this; // 'this' is undefined is function
      // tslint:disable-next-line
      const onOrderComplete = function () {
        that.ngZone.run(() => {
          that.router.navigate(['/register/success']);
        });
      };
      this.ngZone.runOutsideAngular(() => {
        registerEvent((this.registration.settings as IRegistrationEventbrite).eventbriteEventId, onOrderComplete);
      });
    }
  }

  ngOnInit(): void {
    this.customAppService.getCustomAppListener().subscribe(async (app) => {
      this.customApp = app;
      if (typeof this.customApp.registration === 'string') {
        this.registration = await this.registrationService.getRegistrationById(this.customApp.registration);
      } else {
        this.registration = this.customApp.registration;
      }

      if (!this.registration.enabled) {
        this.router.navigate(['/']);
      }

      this.route.queryParams.subscribe(async (params) => {
        const preview = params.preview;

        if (preview) {
          this.ticketcodeAuthService.logout();
          return;
        }
      });

      if (this.registration.type === RegistrationType.STANDARD || this.registration.type === RegistrationType.EMAILPW) {
        this.settings = this.registration.settings as IRegistrationStandard;
        if (this.settings.passwordSettings) {
          const promptText = this.settings.passwordSettings.promptText;
          const pw = this.settings.passwordSettings.password;

          if (screen.width > 699) {
            this.dialog.open(PasswordDialogComponent, {
              width: '30%',
              data: { text: promptText, error: this.settings.passwordSettings.errorText, pw },
              disableClose: true
            });
          } else {
            this.dialog.open(PasswordDialogComponent, {
              maxWidth: '100vw',
              maxHeight: '100vh',
              height: '100%',
              width: '100%',
              data: { text: promptText, error: this.settings.passwordSettings.errorText, pw },
              disableClose: true
            });
          }
        }

        this.registerForm = new FormGroup({});

        this.settings.fields.forEach((field) => {
          this.registerForm.addControl(field.key, new FormControl(''));

          const control = this.registerForm.get(field.key);
          let validators = [];
          if (field.required) {
            if (field.type === REGISTER_FIELDS_TYPES.CHECKBOX || field.type === REGISTER_FIELDS_TYPES.DATA_PRIVACY) {
              validators.push(Validators.requiredTrue);
            } else {
              validators.push(Validators.required);
            }
          }
          if (field.type === REGISTER_FIELDS_TYPES.EMAIL) {
            validators.push(Validators.email);
          }
          if (field.key === 'password') {
            validators = validators.concat([
              Validators.minLength(12),
              Validators.pattern(/\d/),
              Validators.pattern(/[A-Z]/),
              Validators.pattern(/[a-z]/)
            ]);
          }
          control.setValidators(validators);
        });
      } else if (this.registration.type === RegistrationType.IFRAME) {
        this.url = (this.registration.settings as IRegistrationIFrame).iFrameUrl;
      }

      if (typeof this.customApp.login === 'string') {
        this.login = await this.loginService.getLoginById(this.customApp.login);
      } else {
        this.login = this.customApp.login;
      }
      this.toLogin = this.login.enabled && new Date(this.login.enableTime) <= new Date();
      if (this.ticketcodeAuthService.getIsAuthenticted()) {
        if ((this.customApp.generalSettings as IEventGeneralSettings).enableLP) {
          // the first 7 are always the same like login etc, 8th is the first module. See app-routing for full list of non-dynamic routes
          this.router.navigate([this.router.config[8].path]);
        } else {
          this.router.navigate(['/']);
        }
      }

      this.isLoading = false;
    });
  }

  trimValue(event) {
    if (event.target.id === 'email') {
      event.target.value = event.target.value.trim();
      this.registerForm.get('email')?.setValue(event.target.value);
      this.registerForm.updateValueAndValidity();
    }
  }

  onRegister() {
    this.errorEmailExists = false;
    this.settings = this.registration.settings as IRegistrationStandard;
    if (this.registerForm.invalid) {
      this.clickedRegister = true;
      return;
    }

    this.isLoading = true;
    if (this.registration.type === RegistrationType.STANDARD) {
      const additonalFields: { key: string; value: string }[] = [];
      this.settings.fields.forEach((field) => {
        if (field.type === REGISTER_FIELDS_TYPES.DROPDOWN && field.multiselect) {
          additonalFields.push({ key: field.key, value: this.registerForm.value[field.key].join(',') });
        } else if (
          !(field.type === REGISTER_FIELDS_TYPES.DATA_PRIVACY || field.key === 'email' || field.key === 'name' || field.key === 'surname')
        ) {
          additonalFields.push({ key: field.key, value: this.registerForm.value[field.key] });
        }
      });
      let email: string;
      if (this.registerForm.value.email) {
        email = this.registerForm.value.email.toString().trim();
      } else {
        email = 'none' + Date.now().toString();
      }

      if (this.settings.enabledEmail) {
        this.attendeeService
          .register(this.registerForm.value.name, this.registerForm.value.surname, email.toLowerCase(), additonalFields)
          .subscribe((response) => {
            if (response.message === 'error') {
              window.alert(response.data);
              this.isLoading = false;
            } else {
              this.isLoading = true;
              this.router.navigate(['/register/success']);
            }
          });
      } else {
        this.attendeeService
          .registerWithoutTicketcode(this.registerForm.value.name, this.registerForm.value.surname, email, additonalFields)
          .subscribe((response) => {
            if (response.message === 'error') {
              if (response.data === 'invalid data: attendee exists for this event') {
                this.errorEmailExists = true;
                this.isLoading = false;
              } else {
                window.alert(response.data);
                this.isLoading = false;
              }
            } else {
              this.isLoading = true;
              this.ticketcodeAuthService.login(response.data.ticketcode, this.customApp.eventId).subscribe(async () => {
                await this.initAppService.loadRemainingData(true);
                if ((this.customApp.generalSettings as IEventGeneralSettings).enableLP) {
                  // the first 7 are always the same like login etc, 8th is the first module.
                  // See app-routing for full list of non-dynamic routes
                  this.router.navigate([this.router.config[8].path]);
                } else {
                  this.router.navigate(['/']);
                }
              });
            }
          });
      }
    } else if (this.registration.type === RegistrationType.EMAILPW) {
      const additonalFields: { key: string; value: string }[] = [];
      let password = '';
      this.settings.fields.forEach((field) => {
        if (field.key === 'password') {
          password = this.registerForm.value[field.key];
        } else if (field.type === REGISTER_FIELDS_TYPES.DROPDOWN && field.multiselect) {
          additonalFields.push({ key: field.key, value: this.registerForm.value[field.key].join(',') });
        } else if (
          !(field.type === REGISTER_FIELDS_TYPES.DATA_PRIVACY || field.key === 'email' || field.key === 'name' || field.key === 'surname')
        ) {
          additonalFields.push({ key: field.key, value: this.registerForm.value[field.key] });
        }
      });
      let email: string;
      if (this.registerForm.value.email) {
        email = this.registerForm.value.email.toString().trim();
      } else {
        email = 'none' + Date.now().toString();
      }
      this.attendeeService
        .register(this.registerForm.value.name, this.registerForm.value.surname, email.toLowerCase(), additonalFields, password)
        .subscribe((response) => {
          if (response.message === 'error') {
            if (response.data === 'invalid data: attendee exists for this event') {
              this.errorEmailExists = true;
              this.isLoading = false;
            } else {
              window.alert(response.data);
              this.isLoading = false;
            }
          } else {
            this.isLoading = true;
            this.router.navigate(['/register/success']);
          }
        });
    }
  }
}
